import { useCallback, useEffect, useRef, useState } from "react"
import { useSelector } from "react-redux"
import { Link } from "react-router-dom"
import { useReactToPrint } from "react-to-print"
import * as XLSX from "xlsx"
import CommonSelect, { Option } from "../../../core/common/commonSelect"
import Table from "../../../core/common/dataTable/index"
import ImageWithBasePath from "../../../core/common/imageWithBasePath"
import TooltipOption from "../../../core/common/tooltipOption"
import { selectCurrentSession } from "../../../core/data/redux/slice/sessionsSlice"
import { selectCurrentBranch } from "../../../core/data/redux/slice/branchesSlice"
import { campusBrandName, campusBranchLabel } from "../../../utils/campusLogos"
import { selectSystemsForCurrentBranch } from "../../../core/data/redux/slice/systemsSlice"
import { Class, getAllClasses } from "../../../service/classes"
import { getAllFeeGroups } from "../../../service/fee-group"
import { all_routes } from "../../router/all_routes"
import CollectFeesModal from "../feescollection/collectFeesModal"
import StruckOffReviewModal from "./StruckOffReviewModal"
import { getPendingCount } from "../../../service/struck-off-review"
import {
  getDefaulters,
  Defaulter,
  PendingFee,
} from "../../../service/defaulters"

interface Student {
  _id: string
  name: string
  admissionNumber: string
  class: string | { name: string; _id?: string }
  section: string | { name: string; _id?: string }
  grNumber: string
  user: {
    firstName: string
    lastName: string
    profilePicture: string
  }
  status: string
  createdAt: string
  pendingFees: PendingFee[]
  // Carried through from the backend's already-correct per-student
  // computation (distinct unpaid months, not a raw pendingFees count) --
  // getNoticeTypeForStudent prefers this and only falls back to counting
  // when it's missing.
  noticeType?: string
}

interface AppliedFilters {
  classId?: string
  section?: string
  status?: string
  system?: string
  feeGroup?: string
}

interface SingleNoticeProps {
  student: Student
  getCurrentDate: () => string
  noticeType?: string
  dueDate?: Date | null
  compact?: boolean
}

interface PrintNoticeProps {
  student?: Student
  isAllStudents: boolean
}

const NOTICE_TYPE_OPTIONS = [
  { label: "All Notices", value: "all" },
  { label: "First Notice", value: "first" },
  { label: "Second Notice", value: "second" },
  { label: "Third Notice", value: "third" },
]

// Mirrors the only two statuses the backend ever returns here (TC/left-
// without-information are never chased for money, so defaulter-listing.js
// already excludes them before this page sees any data).
const STATUS_OPTIONS = [
  { label: "All (Active + Struck off)", value: "all" },
  { label: "Active", value: "admitted" },
  { label: "Struck off", value: "struck_off" },
]

const getClassName = (classData: any): string => {
  if (!classData) return "N/A"
  if (typeof classData === "string") return classData
  if (typeof classData === "object" && classData.name) return classData.name
  return "N/A"
}

const getSectionName = (sectionData: any): string => {
  if (!sectionData) return "N/A"
  if (typeof sectionData === "string") return sectionData
  if (typeof sectionData === "object" && sectionData.name)
    return sectionData.name
  return "N/A"
}

const DefaultersList = () => {
  const routes = all_routes
  const currentSession = useSelector(selectCurrentSession)
  const currentBranch = useSelector(selectCurrentBranch)

  // Failing quietly is right here: a banner that cannot load its count should
  // simply not appear, never block the defaulters list behind an error.
  const loadPendingStruckOff = () =>
    getPendingCount(currentBranch?._id)
      .then((r) => setPendingStruckOff(r?.count || 0))
      .catch(() => setPendingStruckOff(0))

  useEffect(() => {
    loadPendingStruckOff()
  }, [currentBranch?._id]) // eslint-disable-line react-hooks/exhaustive-deps
  const systems = useSelector(selectSystemsForCurrentBranch)
  const componentRef = useRef<HTMLDivElement>(null)

  const [data, setData] = useState<Defaulter[]>([])
  // How many names the monthly sweep is holding for a decision. Drives the
  // banner — zero means the banner never renders.
  const [pendingStruckOff, setPendingStruckOff] = useState(0)
  const [showStruckOffReview, setShowStruckOffReview] = useState(false)
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null)
  const [searchText, setSearchText] = useState("")
  // Full Class objects (not just {label,value}) so Section options can be
  // derived from the selected class's own `sections` array -- same pattern
  // as fee-sticker-print, scoped to session/campus already by the backend.
  const [classes, setAllClasses] = useState<Class[]>([])
  const [classesLoading, setClassesLoading] = useState(false)
  const [feeGroups, setFeeGroups] = useState<Option[]>([])
  const [loading, setLoading] = useState(false)
  const [totalDefaulters, setTotalDefaulters] = useState(0)
  const [totalPendingAmount, setTotalPendingAmount] = useState(0)
  const [selectedDueDate, setSelectedDueDate] = useState<Date | null>(null)
  const [selectedNoticeType, setSelectedNoticeType] = useState<string>("first")
  const [selectedStatus, setSelectedStatus] = useState<string>("all")

  // No System pre-selected -- nothing loads until the admin actually picks
  // one. Previously defaulted to systems[0] (and a later fix, commit
  // 5f2cde8, made sure that default actually landed once the systems list
  // finished loading async from Redux) -- both eagerly fetched every
  // defaulter for the whole first Board System on page open, which is
  // exactly what this screen should NOT do per explicit request.
  const [appliedFilters, setAppliedFilters] = useState<AppliedFilters | null>({
    feeGroup: "all",
  })

  // Draft filters (fee-sticker-print pattern) -- nothing fetches until
  // Search is clicked. Class is optional here (unlike fee-sticker-print):
  // System alone is enough to search, and an empty Class means "every
  // class under this System".
  const [draftSystem, setDraftSystem] = useState<string | undefined>(undefined)
  const [draftClassId, setDraftClassId] = useState<string | undefined>(undefined)
  const [draftSection, setDraftSection] = useState<string | undefined>(undefined)
  const [hasSearched, setHasSearched] = useState(false)
  const [quickSearchValue, setQuickSearchValue] = useState("")

  // Rows explicitly checked in the table -- when non-empty, Print All prints
  // only these instead of every loaded record.
  const [selectedDefaulters, setSelectedDefaulters] = useState<Defaulter[]>([])
  const [showPrintNotice, setShowPrintNotice] = useState(false)
  // Drives whether the Action column even exists in the DOM for this print
  // job -- CSS alone (display:none on the column, table-layout:auto,
  // knocking out antd's inline min-width on every wrapper) still left a
  // blank strip where it used to be, so instead of fighting antd's internals
  // further the column is simply omitted from `columns` while this is true.
  // Screen view is untouched -- this only flips true for the duration of
  // the roster print/PDF.
  const [isPrintingList, setIsPrintingList] = useState(false)
  const [selectedStudentForPrint, setSelectedStudentForPrint] =
    useState<Student | null>(null)
  const [printAllStudents, setPrintAllStudents] = useState(false)

  const noticePrintRef = useRef<HTMLDivElement>(null)

  // Class options scoped to whichever System is currently selected -- fixes
  // the Class filter previously showing every board's classes mixed
  // together (Matric and AKU both have classes literally named "Class IX"/
  // "Class X" with different ids, so picking from the unfiltered list could
  // silently apply the wrong one).
  const classOptions: Option[] = classes.map(cls => ({
    label: cls.name,
    value: cls._id,
  }))

  // Sections come from the selected class's own `sections` array (already
  // scoped to this campus/session by the backend), not a separate global
  // section catalog -- same pattern as fee-sticker-print.
  const sectionOptions: Option[] = (() => {
    const selectedClass = classes.find(c => c._id === draftClassId)
    return (selectedClass?.sections || []).map((section: any) => ({
      label: section.name,
      value: section._id,
    }))
  })()

  const buildQueryParams = useCallback(() => {
    const queryParams: Record<string, any> = {
      // Always fetch everything -- no pagination on this screen (Print All
      // must print every matching record, not just one page's worth).
      page: 1,
      limit: 10000,
      sessionId: currentSession?._id,
    }

    if (appliedFilters?.system) {
      queryParams.systemId = appliedFilters.system
    }

    if (appliedFilters?.classId) {
      queryParams.classId = appliedFilters.classId
    }

    if (appliedFilters?.section) {
      queryParams.sectionId = appliedFilters.section
    }

    if (appliedFilters?.feeGroup && appliedFilters.feeGroup !== "all") {
      queryParams.feeGroupId = appliedFilters.feeGroup
    }

    if (searchText) {
      queryParams.searchText = searchText
    }

    return queryParams
  }, [appliedFilters, searchText, currentSession?._id])

  const filterDataByNoticeType = useCallback(
    (data: Defaulter[], noticeType: string): Defaulter[] => {
      if (!noticeType || noticeType === "all") return data

      return data.filter((defaulter: Defaulter) => {
        const defaulterNoticeType = defaulter.noticeType

        if (defaulterNoticeType) {
          return defaulterNoticeType === noticeType
        }

        // Same distinct-month fix as getNoticeTypeForStudent -- a raw
        // pendingFees count now overcounts since Late Fee is its own entry.
        const distinctMonths = new Set(
          (defaulter.pendingFees || [])
            .filter((fee: PendingFee) => fee.month && fee.month !== "N/A")
            .map((fee: PendingFee) => fee.month),
        )
        const tuitionMonthsCount = distinctMonths.size

        if (noticeType === "first") {
          return tuitionMonthsCount === 1
        } else if (noticeType === "second") {
          return tuitionMonthsCount === 2
        } else if (noticeType === "third") {
          return tuitionMonthsCount >= 3
        }
        return true
      })
    },
    [],
  )

  const fetchAllData = useCallback(async () => {
    setLoading(true)
    try {
      // Fetch all data without pagination to filter properly by notice type
      const queryParams = {
        ...buildQueryParams(),
        page: 1,
        limit: 10000, // Fetch all to filter properly on frontend
      }

      const response = await getDefaulters(queryParams)

      // Handle response - backend may or may not have success field
      const defaultersData =
        response.success !== undefined && response.success
          ? response.data
          : response.data || []

      if (defaultersData && Array.isArray(defaultersData)) {
        let allDefaulters = defaultersData.filter(
          (defaulter: Defaulter) => defaulter && defaulter.name,
        )

        // Filter by notice type (due date is only for print display, has no effect on data)
        allDefaulters = filterDataByNoticeType(
          allDefaulters,
          selectedNoticeType,
        )

        if (selectedStatus !== "all") {
          allDefaulters = allDefaulters.filter(
            (defaulter: Defaulter) => defaulter.status === selectedStatus,
          )
        }

        // No pagination on this screen -- every matching record shows and
        // prints at once.
        const total = allDefaulters.length
        setData(allDefaulters)
        // A fresh search invalidates any prior checkbox selection.
        setSelectedDefaulters([])

        const totalPending = allDefaulters.reduce(
          (sum: number, defaulter: Defaulter) =>
            sum +
            (typeof defaulter.totalPendingAmount === "number"
              ? defaulter.totalPendingAmount
              : (defaulter.pendingFees || []).reduce(
                  (feeSum: number, fee: PendingFee) =>
                    feeSum + (fee.amount || 0),
                  0,
                )),
          0,
        )

        setTotalDefaulters(total)
        setTotalPendingAmount(totalPending)
      } else {
        setData([])
        setTotalDefaulters(0)
        setTotalPendingAmount(0)
      }
    } catch (error) {
      console.error("Error fetching defaulters data:", error)
    } finally {
      setLoading(false)
    }
  }, [buildQueryParams, selectedNoticeType, selectedStatus, filterDataByNoticeType])

  // Scoped to the currently selected Board System -- fixes the Class filter
  // previously mixing every board's classes together (see note above
  // classOptions).
  const fetchClasses = async (systemId: string | undefined) => {
    if (!systemId) {
      setAllClasses([])
      return
    }
    setClassesLoading(true)
    try {
      const response = await getAllClasses({
        system: systemId,
        session: currentSession?._id,
        limit: 200,
      })
      setAllClasses(response.classes || [])
    } catch (error) {
      console.error("Error fetching classes:", error)
    } finally {
      setClassesLoading(false)
    }
  }

  const fetchFeeGroups = async () => {
    try {
      const response = await getAllFeeGroups({
        session: currentSession?._id,
        system: draftSystem,
      })

      const allFeesOption = {
        _id: "all",
        name: "All Fees",
        label: "All Fees",
        value: "all",
      }
      const formattedFeeGroups = [allFeesOption, ...response.data]

      setFeeGroups(formattedFeeGroups)

      // Defaults to "All Fees" -- used to silently default to Tuition only,
      // which hid every one-time due (Re-Admission Fee, Transfer Fee, etc.)
      // from this page with no visible control to notice or change it (there
      // is no Fee Group dropdown rendered on this page at all).
      if (!appliedFilters?.feeGroup) {
        setAppliedFilters(prev => ({
          ...(prev || {}),
          feeGroup: "all",
        }))
      }
    } catch (error) {
      console.error("Error fetching fee groups:", error)
    }
  }

  useEffect(() => {
    fetchFeeGroups()
  }, [currentSession?._id, draftSystem])

  // Nothing fetches until Search (or Quick Search) is explicitly clicked --
  // after that, changing Notice/Due Date/sort re-fetches against whatever
  // was last searched, same as before.
  useEffect(() => {
    if (!hasSearched) return
    fetchAllData()
  }, [hasSearched, appliedFilters, searchText, selectedNoticeType, selectedStatus, fetchAllData])

  // Refetch Class options whenever the DRAFT System changes -- and reset the
  // draft Class/Section so a stale selection from a previous System can't
  // linger (same cascade pattern as fee-sticker-print).
  useEffect(() => {
    fetchClasses(draftSystem)
  }, [draftSystem]) // eslint-disable-line react-hooks/exhaustive-deps

  // Draft-only -- nothing is fetched until Search is clicked.
  const handleSystemChange = (systemItem: any) => {
    setDraftSystem(systemItem?._id || undefined)
    setDraftClassId(undefined)
    setDraftSection(undefined)
  }

  const handleClassChange = (option: any) => {
    setDraftClassId((option?.value as string) || undefined)
    setDraftSection(undefined)
  }

  const handleSectionChange = (option: any) => {
    setDraftSection((option?.value as string) || undefined)
  }

  // System is the only requirement to search -- Class/Section are optional
  // and, left blank, mean "every class/section under this System".
  const canSearch = Boolean(draftSystem)

  const handleSearchClick = () => {
    if (!canSearch) return
    setAppliedFilters(prev => ({
      ...(prev || {}),
      system: draftSystem,
      classId: draftClassId,
      section: draftSection,
    }))
    setQuickSearchValue("")
    setSearchText("")
    setHasSearched(true)
  }

  // Bypasses System/Class entirely -- searches by GR number or name across
  // every class/system in the current session at once (server-side, see
  // getDefaulters' searchText handling).
  const handleQuickSearch = () => {
    const value = quickSearchValue.trim()
    if (!value) return
    setDraftSystem(undefined)
    setDraftClassId(undefined)
    setDraftSection(undefined)
    setAppliedFilters(prev => ({
      ...(prev || {}),
      system: undefined,
      classId: undefined,
      section: undefined,
    }))
    setSearchText(value)
    setHasSearched(true)
  }

  const handlePrint = useReactToPrint({
    contentRef: componentRef,
    pageStyle: `
      @page {
        size: A4 landscape;
        margin: 8mm;
      }
      * {
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
      }
      html, body {
        width: 100% !important;
        margin: 0 !important;
        padding: 0 !important;
        background-color: #fff !important;
      }
      .printable-content {
        position: static !important;
        width: 100% !important;
        max-width: 100% !important;
        padding: 0 !important;
        margin: 0 !important;
      }
      .printable-content .print-only-header {
        display: flex !important;
        visibility: visible !important;
        justify-content: space-between !important;
        align-items: center !important;
        width: 100% !important;
        margin-bottom: 8px !important;
        padding-bottom: 5px !important;
        border-bottom: 2px solid #2980b9 !important;
      }
      .printable-content .ant-table-wrapper,
      .printable-content .ant-table,
      .printable-content .ant-table-container,
      .printable-content .ant-table-content,
      .printable-content table {
        width: 100% !important;
        min-width: 100% !important;
        max-width: 100% !important;
      }
      .printable-content table {
        font-size: 10px !important;
        border-collapse: collapse !important;
        table-layout: auto !important;
      }
      .printable-content colgroup,
      .printable-content col {
        display: none !important;
      }
      /* Wide text (Due Months' comma-separated list) was the other half of
         the page-width overflow, alongside the Name column's avatar image --
         let it wrap instead of forcing the table wider than the page. */
      .printable-content td {
        white-space: normal !important;
        word-break: break-word !important;
      }
      .print-only-total {
        width: 100% !important;
        display: block !important;
        text-align: right !important;
        font-weight: bold !important;
        font-size: 11px !important;
        margin-top: 8px !important;
        padding-top: 6px !important;
        border-top: 2px solid #2980b9 !important;
      }
      .printable-content th,
      .printable-content td {
        padding: 3px 4px !important;
        border: 1px solid #ccc !important;
      }
      .printable-content th {
        background-color: #2980b9 !important;
        color: white !important;
      }
      .printable-content .print-col {
        display: table-cell !important;
        visibility: visible !important;
      }
      .printable-content th.no-print,
      .printable-content td.no-print,
      .table-top-data,
      .ant-table-selection-column,
      .ant-table-selection,
      .no-print {
        display: none !important;
        border: none !important;
        padding: 0 !important;
        width: 0 !important;
      }
    `,
    onAfterPrint: () => setIsPrintingList(false),
  })

  // Flip isPrintingList on first so `columns` (and therefore the rendered
  // table) drops Action before react-to-print captures the content, then
  // give React a tick to re-render -- same "give React a tick" pattern
  // already used for the notice prints below.
  const triggerListPrint = () => {
    setIsPrintingList(true)
    setTimeout(() => {
      handlePrint()
    }, 50)
  }

  const handlePrintSingleNoticeA5 = useReactToPrint({
    contentRef: noticePrintRef,
    ignoreGlobalStyles: true, // Only apply CSS defined in pageStyle (no global SCSS/styles)
    pageStyle: `
      @page {
        size: A5 portrait;
        margin: 0mm;
      }
      html, body {
        padding: 0 !important;
        margin: 0 !important;
      }
      /* Keep A5 content bounded: prevents 2nd blank page */
      .notice-printable-content {
        width: 148mm !important;  /* A5 portrait width */
        height: 210mm !important; /* A5 portrait height */
        max-height: 210mm !important;
        overflow: hidden !important;
        margin: 0 !important;
        padding: 0 !important;
      }
      .notice-page {
        break-after: page;
        page-break-after: always;
      }
      * {
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
    `,
    onAfterPrint: () => {
      setShowPrintNotice(false)
    },
  })

  const handlePrintNoticeAllA4 = useReactToPrint({
    contentRef: noticePrintRef,
    ignoreGlobalStyles: true, // Only apply CSS defined in pageStyle (no global SCSS/styles)
    pageStyle: `
      @page {
        size: A4 portrait;
        margin: 10mm;
      }
      html, body {
        padding: 0 !important;
        margin: 0 !important;
      }
      .notice-page {
        break-after: page;
        page-break-after: always;
      }
      * {
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
    `,
    onAfterPrint: () => {
      setShowPrintNotice(false)
    },
  })

  const getNoticeTypeForStudent = (student: Student): string => {
    // Prefer the backend's own computation -- it counts DISTINCT unpaid
    // months. Falling back to counting pendingFees entries directly (below)
    // now overcounts: since Late Fee split out into its own entry, one
    // unpaid month can be 2 entries (Tuition Fee + Late Fee), which used to
    // silently push a 1-month defaulter into "Second Notice".
    if (student.noticeType) return student.noticeType

    const distinctMonths = new Set(
      (student.pendingFees || [])
        .filter((fee: PendingFee) => fee.month && fee.month !== "N/A")
        .map((fee: PendingFee) => fee.month),
    )

    if (distinctMonths.size === 1) return "first"
    if (distinctMonths.size === 2) return "second"
    return "third"
  }

  const handlePrintSingleNotice = (student: Student) => {
    if (!student) return
    setSelectedStudentForPrint(student)
    setPrintAllStudents(false)

    const autoNoticeType = getNoticeTypeForStudent(student)
    setSelectedNoticeType(autoNoticeType)

    setShowPrintNotice(true)
    // Give React a tick to render the print content
    setTimeout(() => {
      handlePrintSingleNoticeA5()
    }, 100)
  }

  // Prints whichever rows are checked; if nothing is checked, prints every
  // loaded record (the original "print all" behavior).
  const recordsToPrint = selectedDefaulters.length > 0 ? selectedDefaulters : data
  const canPrintAllNotices = Boolean(selectedDueDate && recordsToPrint.length > 0)

  const handlePrintAllNotices = () => {
    if (recordsToPrint.length === 0) return
    setPrintAllStudents(true)
    setShowPrintNotice(true)
    // Give React a tick to render the print content
    setTimeout(() => {
      handlePrintNoticeAllA4()
    }, 100)
  }

  const handleExportExcel = () => {
    // `data` is already the full matching set -- this screen has no
    // pagination, so nothing extra needs fetching for a "complete" export.
    const rows = data.map(defaulter => {
      const totalPending =
        defaulter.totalPendingAmount ??
        (defaulter.pendingFees || []).reduce(
          (sum: number, fee: PendingFee) => sum + (fee.amount || 0),
          0,
        )
      // Distinct months owed, not a raw pendingFees count -- same fix as
      // the table's "Due Fees" badge (Late Fee is its own entry now).
      const monthsOwed = new Set(
        (defaulter.pendingFees || [])
          .filter((fee: PendingFee) => fee.month && fee.month !== "N/A")
          .map((fee: PendingFee) => fee.month),
      ).size || defaulter.pendingFees?.length || 0
      return {
        "GR No": defaulter.grNumber || "N/A",
        Name: defaulter.name || "N/A",
        "Admission Number": defaulter.grNumber || "N/A",
        Class: defaulter.class || "N/A",
        Section: defaulter.section || "N/A",
        "Last Attended": defaulter.lastAttendanceDate
          ? new Date(defaulter.lastAttendanceDate).toLocaleDateString("en-GB")
          : "N/A",
        "Months Owed": monthsOwed,
        "Total Pending Amount": totalPending,
      }
    })

    const grandTotal = rows.reduce((sum, r) => sum + r["Total Pending Amount"], 0)
    rows.push({
      "GR No": "",
      Name: "",
      "Admission Number": "",
      Class: "",
      Section: "",
      "Last Attended": "",
      "Months Owed": "TOTAL" as any,
      "Total Pending Amount": grandTotal,
    })

    const ws = XLSX.utils.json_to_sheet(rows)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, "Defaulters")
    XLSX.writeFile(
      wb,
      `defaulters_export_${new Date().toISOString().split("T")[0]}.xlsx`,
    )
  }

  const handleResetFilters = () => {
    // Matches the initial (no System picked) state -- resetting shouldn't
    // eagerly reload everything for the first System either.
    setDraftSystem(undefined)
    setDraftClassId(undefined)
    setDraftSection(undefined)
    setAppliedFilters({
      feeGroup: "all",
    })
    setSelectedStatus("all")
    setQuickSearchValue("")
    setSearchText("")
    setHasSearched(false)
    setData([])
  }

  const splitName = (fullName: string | null | undefined) => {
    if (!fullName) return { firstName: "", lastName: "" }

    const nameParts = fullName.trim().split(" ")
    const firstName = nameParts[0] || ""
    const lastName = nameParts.slice(1).join(" ") || ""

    return { firstName, lastName }
  }

  const convertDefaulterForModal = (defaulter: Defaulter): any => {
    const { firstName, lastName } = splitName(defaulter.name || "")

    return {
      _id: defaulter.enrollmentId || defaulter._id || "",
      grNumber: defaulter.grNumber || "N/A",
      student: {
        grNumber: defaulter.grNumber || "N/A",
        _id: defaulter._id || "",
      },
      user: {
        firstName,
        lastName,
        profilePicture: "assets/img/students/student-default.jpg",
      },
      class: {
        name: defaulter.class || "N/A",
        _id: defaulter.classId || "",
      },
      section: {
        name: defaulter.section || "N/A",
        _id: defaulter.sectionId || "",
      },
      name: defaulter.name || "Unknown",
      admissionNumber: defaulter.grNumber || "N/A",
      status: "active",
      createdAt: new Date().toISOString(),
      pendingFees: defaulter.pendingFees || [],
      noticeType: defaulter.noticeType,
    }
  }

  const convertDefaulterForPrint = (defaulter: Defaulter): Student => {
    const { firstName, lastName } = splitName(defaulter.name || "")

    return {
      _id: defaulter._id || "",
      name: defaulter.name || "Unknown",
      admissionNumber: defaulter.grNumber || "N/A",
      class: defaulter.class || "N/A",
      section: defaulter.section || "N/A",
      grNumber: defaulter.grNumber || "N/A",
      user: {
        firstName,
        lastName,
        profilePicture: "assets/img/students/student-default.jpg",
      },
      status: "active",
      createdAt: new Date().toISOString(),
      pendingFees: defaulter.pendingFees || [],
      noticeType: defaulter.noticeType,
    }
  }

  const SingleNotice = ({
    student,
    getCurrentDate,
    noticeType = "first",
    dueDate,
    compact = false,
  }: SingleNoticeProps) => {
    const totalAmount =
      student?.pendingFees?.reduce(
        (sum: number, fee: PendingFee) => sum + fee.amount,
        0,
      ) || 0

    const className = getClassName(student?.class)
    const sectionName = getSectionName(student?.section)

    const noticeNumber =
      noticeType === "first" ? "01" : noticeType === "second" ? "02" : "03"

    const feesByMonth =
      student?.pendingFees?.reduce((acc: any, fee: PendingFee) => {
        let monthKey = fee.month
        if (!monthKey || monthKey === "N/A") {
          monthKey = fee.feeGroup || "Other"
        }
        if (!acc[monthKey]) {
          acc[monthKey] = []
        }
        acc[monthKey].push(fee)
        return acc
      }, {}) || {}

    // Due date as it appears in the body text -- "07 Sep, 2026", distinct
    // from the header's numeric "Date:" format (which is always today's
    // date, never this). Built manually rather than via toLocaleDateString
    // so the "Sep" abbreviation and comma are guaranteed, not
    // locale/engine-dependent.
    const SHORT_MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]
    const formatDueDate = (d: Date) =>
      `${String(d.getDate()).padStart(2, "0")} ${SHORT_MONTHS[d.getMonth()]}, ${d.getFullYear()}`

    return (
      <div
        style={{
          padding: compact ? "6px" : "20px",
          fontFamily: "Arial, sans-serif",
        }}
      >
        <div style={{ textAlign: "center", marginBottom: "20px" }}>
          {currentBranch?.blackLogoUrl ? (
            <img
              src={currentBranch.blackLogoUrl}
              alt=""
              style={{ width: "60px", height: "60px", objectFit: "contain" }}
            />
          ) : null}
          <h1
            style={{
              fontSize: "24px",
              fontWeight: "bold",
              margin: "10px 0",
              textTransform: "uppercase",
            }}
          >
            {(currentBranch?.schoolName || currentBranch?.name || "METROPOLITAN ACADEMY").toUpperCase()}
          </h1>
          <p
            style={{
              margin: "5px 0",
              fontSize: "12px",
              color: "#666",
            }}
          >
            {currentBranch?.address || "Head Office / Campus I, B-33 Block 13, F.B. Area, Karachi, (0092) 21 36363629"}
          </p>
        </div>

        {/* Notice Title Bar */}
        <div
          style={{
            backgroundColor: "#000",
            color: "#fff",
            textAlign: "center",
            padding: "10px",
            marginBottom: "20px",
            fontWeight: "bold",
            fontSize: "16px",
          }}
        >
          Fee Non Payment Reminder Notice
        </div>

        {/* Reference and Date */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: "15px",
            fontSize: "14px",
          }}
        >
          <p style={{ margin: "0" }}>
            <strong>MPA/CIR/REF/2026#Circular-{noticeNumber}</strong>
          </p>
          <p style={{ margin: "0" }}>
            {/* Always today's date -- the day the notice is generated/printed.
                Never the due date, which belongs in the body text below. */}
            <strong>Date: {getCurrentDate()}</strong>
          </p>
        </div>

        {/* Salutation and Body */}
        <div style={{ marginBottom: "15px", fontSize: "14px" }}>
          <p style={{ marginBottom: "5px" }}>Dear Parents/Guardian</p>
          <p style={{ marginBottom: "10px" }}>Assalam-o-Alaikum</p>

          {noticeType === "first" && (
            <p style={{ lineHeight: "1.6" }}>
              It is brought into your notice that the tuition fee of your ward
              named <strong>{student?.name || "N/A"}</strong> of class{" "}
              <strong>{className}</strong> section{" "}
              <strong>{sectionName}</strong> having GR#{" "}
              <strong>{student?.grNumber || "N/A"}</strong> for the following
              month, amounting to Rs.{" "}
              <strong>{totalAmount.toLocaleString()}/-</strong> has not been
              paid. It is requested to pay the subject fee dues
              {dueDate ? <> by <strong>{formatDueDate(dueDate)}</strong></> : null}.
            </p>
          )}

          {noticeType === "second" && (
            <p style={{ lineHeight: "1.6" }}>
              It is reminded to you that the tuition fee of your ward named{" "}
              <strong>{student?.name || "N/A"}</strong> of class{" "}
              <strong>{className}</strong> section{" "}
              <strong>{sectionName}</strong> having GR#{" "}
              <strong>{student?.grNumber || "N/A"}</strong> for the following
              month, amounting to Rs.{" "}
              <strong>{totalAmount.toLocaleString()}/-</strong> is still
              awaited, kindly settle down the outstanding dues
              {dueDate ? <> by date <strong>{formatDueDate(dueDate)}</strong></> : null}.
            </p>
          )}

          {noticeType === "third" && (
            // Same opening as Notice 2 ("reminded to you...") -- the two
            // only diverge after the amount: Notice 2 closes here with its
            // own due-date sentence, Notice 3 instead runs straight on into
            // the severe paragraph below (one continuous sentence, hence no
            // full stop -- unchanged from before).
            <p style={{ lineHeight: "1.6" }}>
              It is reminded to you that the tuition fee of your ward named{" "}
              <strong>{student?.name || "N/A"}</strong> of class{" "}
              <strong>{className}</strong> section{" "}
              <strong>{sectionName}</strong> having GR#{" "}
              <strong>{student?.grNumber || "N/A"}</strong> for the following
              month, amounting to Rs.{" "}
              <strong>{totalAmount.toLocaleString()}/-</strong>
            </p>
          )}
        </div>

        {/* Fee Details Table */}
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            marginBottom: "20px",
            fontSize: "14px",
          }}
        >
          <thead>
            <tr>
              <th
                style={{
                  padding: "8px",
                  textAlign: "left",
                  border: "1px solid #000",
                  fontWeight: "bold",
                }}
              >
                Month
              </th>
              <th
                style={{
                  padding: "8px",
                  textAlign: "left",
                  border: "1px solid #000",
                  fontWeight: "bold",
                }}
              >
                Description
              </th>
              <th
                style={{
                  padding: "8px",
                  textAlign: "left",
                  border: "1px solid #000",
                  fontWeight: "bold",
                }}
              >
                Amount
              </th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(feesByMonth).map(
              ([month, fees]: [string, any], idx: number) => {
                const monthTotal = fees.reduce(
                  (sum: number, fee: PendingFee) => sum + (fee.amount || 0),
                  0,
                )
                const tuitionFee = fees.find((f: PendingFee) =>
                  f.feeType?.toLowerCase().includes("tuition"),
                )
                const lateFee = fees.find((f: PendingFee) =>
                  f.feeType?.toLowerCase().includes("late"),
                )
                // Any OTHER fee type billed for this same month (e.g. STEAM
                // Education Fee) -- used to be silently dropped from the
                // text here even though the Amount column (monthTotal,
                // summed from every fee in the group) already included it.
                const otherFeesThisMonth = fees.filter(
                  (f: PendingFee) => f !== tuitionFee && f !== lateFee,
                )

                return (
                  <tr key={idx}>
                    <td
                      style={{
                        padding: "8px",
                        border: "1px solid #000",
                        verticalAlign: "top",
                      }}
                    >
                      {month}
                    </td>
                    <td style={{ padding: "8px", border: "1px solid #000" }}>
                      {tuitionFee && (
                        <>
                          Tuition Fee:{" "}
                          {(tuitionFee.amount || 0).toLocaleString()}
                          <br />
                        </>
                      )}
                      {lateFee && (
                        <>
                          Late Fee: {(lateFee.amount || 0).toLocaleString()}
                          {otherFeesThisMonth.length > 0 && <br />}
                        </>
                      )}
                      {otherFeesThisMonth.map((fee: PendingFee, i: number) => (
                        <div key={i}>
                          {fee.feeType}: {(fee.amount || 0).toLocaleString()}
                          {i < otherFeesThisMonth.length - 1 && <br />}
                        </div>
                      ))}
                    </td>
                    <td
                      style={{
                        padding: "8px",
                        border: "1px solid #000",
                        verticalAlign: "top",
                      }}
                    >
                      Rs {monthTotal.toLocaleString()}/-
                    </td>
                  </tr>
                )
              },
            )}
          </tbody>
          <tfoot>
            <tr>
              <td
                style={{
                  padding: "8px",
                  textAlign: "right",
                  border: "1px solid #000",
                  fontWeight: "bold",
                }}
                colSpan={2}
              >
                Total
              </td>
              <td
                style={{
                  padding: "8px",
                  border: "1px solid #000",
                  fontWeight: "bold",
                }}
              >
                Rs {totalAmount.toLocaleString()}/-
              </td>
            </tr>
          </tfoot>
        </table>

        {/* Additional text for Third Notice only */}
        {noticeType === "third" && (
          <div
            style={{
              marginBottom: "20px",
              fontSize: "14px",
              lineHeight: "1.6",
            }}
          >
            <p style={{ marginBottom: "10px" }}>
              has still not been received which has forced the management to
              struck off{" "}
              {student?.user?.firstName &&
              (student.user.firstName.toLowerCase().endsWith("a") ||
                student.user.firstName.toLowerCase().endsWith("i"))
                ? "She"
                : "He"}{" "}
              will not be permitted to appear in First Periodical/ Half
              yearly/Second Periodical/ Annual Examination, school should not be
              held responsible in case of default.
            </p>
            <p>
              You are directed to pay off the unsettled amount at the earliest,
              otherwise school decision will be final.
            </p>
          </div>
        )}

        {/* Principal Signature */}
        <div
          style={{
            textAlign: "right",
            marginTop: compact ? "20px" : "30px",
            marginBottom: compact ? "20px" : "40px",
          }}
        >
          <p style={{ margin: "0", fontSize: "14px" }}>Sincerely yours,</p>
          <p style={{ margin: "0", fontSize: "14px", marginTop: "5px" }}>
            <strong>Principal</strong>
          </p>
        </div>

        <div
          style={{
            marginTop: compact ? "25px" : "40px",
            paddingTop: compact ? "10px" : "20px",
            borderTop: "2px dashed #000",
          }}
        >
          <p
            style={{
              fontSize: "16px",
              fontWeight: "bold",
              marginBottom: "10px",
            }}
          >
            IMPORTANT
          </p>
          <p style={{ fontSize: "14px", marginBottom: "20px" }}>
            If you have paid the subject amount already please treat this
            circular as cancelled
          </p>

          <div style={{ marginTop: "20px" }}>
            <div style={{ marginBottom: "15px" }}>
              <div
                style={{
                  borderBottom: "1px solid #000",
                  paddingBottom: "5px",
                  marginBottom: "15px",
                }}
              >
                <strong>Name of the student</strong>
              </div>
            </div>
            <div style={{ marginBottom: "15px" }}>
              <div
                style={{
                  borderBottom: "1px solid #000",
                  paddingBottom: "5px",
                  marginBottom: "15px",
                }}
              >
                <strong>Class/Section</strong>
              </div>
            </div>
            <div style={{ marginBottom: "15px" }}>
              <div
                style={{
                  borderBottom: "1px solid #000",
                  paddingBottom: "5px",
                  marginBottom: "15px",
                }}
              >
                <strong>Recipt No.</strong>
              </div>
            </div>
            <div style={{ marginBottom: "15px" }}>
              <div
                style={{
                  borderBottom: "1px solid #000",
                  paddingBottom: "5px",
                  marginBottom: "15px",
                }}
              >
                <strong>Date</strong>
              </div>
            </div>
            <div
              style={{
                marginTop: compact ? "20px" : "30px",
                textAlign: "right",
              }}
            >
              <div
                style={{
                  borderBottom: "1px solid #000",
                  display: "inline-block",
                  minWidth: "200px",
                  paddingBottom: "5px",
                }}
              >
                <strong>Guardian Signature</strong>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const PrintNotice = ({ student, isAllStudents }: PrintNoticeProps) => {
    const getCurrentDate = () => {
      const today = new Date()
      const day = String(today.getDate()).padStart(2, "0")
      const month = String(today.getMonth() + 1).padStart(2, "0")
      const year = today.getFullYear()
      return `${day}-${month}-${year}`
    }

    if (isAllStudents) {
      return (
        <>
          <div ref={noticePrintRef} className="notice-printable-content">
            {recordsToPrint.map((defaulter, index) => {
              const student = convertDefaulterForPrint(defaulter)
              const noticeType = getNoticeTypeForStudent(student)
              return (
                <div
                  key={defaulter._id || index}
                  className="notice-page"
                  style={{
                    pageBreakAfter: "always",
                    padding: "20px",
                    fontFamily: "Arial, sans-serif",
                  }}
                >
                  <SingleNotice
                    student={student}
                    getCurrentDate={getCurrentDate}
                    noticeType={noticeType}
                    dueDate={selectedDueDate}
                  />
                  {index < recordsToPrint.length - 1 && (
                    <div style={{ pageBreakAfter: "always" }}></div>
                  )}
                </div>
              )
            })}
          </div>
        </>
      )
    }

    return (
      <>
        <div ref={noticePrintRef} className="notice-printable-content">
          {student && (
            <SingleNotice
              student={student}
              getCurrentDate={getCurrentDate}
              noticeType={selectedNoticeType}
              dueDate={selectedDueDate}
              compact
            />
          )}
        </div>
      </>
    )
  }

  const allColumns = [
    {
      // Print-only (hidden on screen, same pattern as Student List) -- this
      // page has no pagination, so it's simply the row's position in the
      // full loaded list.
      title: "S.No",
      width: 60,
      className: "print-col",
      onHeaderCell: () => ({ className: "print-col" } as any),
      render: (_: unknown, __: Defaulter, index: number) => index + 1,
    },
    {
      title: "GR No",
      dataIndex: "grNumber",
      width: 120,
      render: (_text: string, record: Defaulter) => {
        return record?.grNumber || "N/A"
      },
    },
    {
      title: "Name",
      dataIndex: "name",
      render: (_text: string, record: Defaulter) => {
        const { firstName, lastName } = splitName(record.name)
        return (
          <div className="d-flex align-items-center">
            {/* Decorative only -- serves no purpose on a printed list and
                was eating into the width that pushed Due Fees off the page. */}
            <Link to="#" className="avatar avatar-md no-print">
              <ImageWithBasePath
                src={"assets/img/students/student-01.jpg"}
                className="img-fluid rounded-circle"
                alt="img"
              />
            </Link>
            <div className="ms-2">
              <p className="text-dark mb-0">
                <Link to={routes.adminDashboard} state={record}>
                  {firstName} {lastName}
                </Link>
              </p>
            </div>
          </div>
        )
      },
    },
    {
      title: "Contact",
      dataIndex: "contactNo",
      width: 120,
      render: (_text: string, record: Defaulter) => record?.contactNo || "N/A",
    },
    {
      title: "Class",
      dataIndex: "class",
      width: 120,
      render: (_text: string, record: Defaulter) => {
        return record?.class || "N/A"
      },
    },
    {
      title: "Section",
      dataIndex: "section",
      width: 120,
      render: (_text: string, record: Defaulter) => {
        return record?.section || "N/A"
      },
    },
    {
      title: "Last Attended",
      dataIndex: "lastAttendanceDate",
      width: 130,
      render: (_text: string, record: Defaulter) => {
        if (!record.lastAttendanceDate) return <span className="text-muted">—</span>
        return new Date(record.lastAttendanceDate).toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "2-digit",
          year: "2-digit",
        })
      },
    },
    {
      title: "Due Months",
      dataIndex: "dueMonths",
      width: 150,
      render: (_text: string, record: Defaulter) => {
        const pendingFees = record.pendingFees || []
        const months: string[] = []
        const feeGroups: string[] = []

        pendingFees.forEach((fee: PendingFee) => {
          if (fee.month && fee.month !== "N/A") {
            // Short form ("Aug 26" not "August 2026") -- with a new Contact
            // column added, the full month names were the single biggest
            // reason this table overflowed the printed page width.
            const d = new Date(`1 ${fee.month}`)
            const short = !isNaN(d.getTime())
              ? d.toLocaleDateString("en-US", { month: "short", year: "2-digit" })
              : fee.month
            if (!months.includes(short)) {
              months.push(short)
            }
          } else {
            if (fee.feeGroup) {
              if (!feeGroups.includes(fee.feeGroup)) {
                feeGroups.push(fee.feeGroup)
              }
            }
          }
        })

        const allItems = [...months, ...feeGroups]

        return (
          <div>
            <span className="text-dark">
              {allItems.length > 0 ? allItems.join(", ") : "N/A"}
            </span>
          </div>
        )
      },
    },
    {
      title: "Due Fees",
      dataIndex: "pendingFees",
      width: 150,
      render: (pendingFees: PendingFee[], _t: unknown, record: Defaulter) => {
        const totalPending =
          typeof record.totalPendingAmount === "number"
            ? record.totalPendingAmount
            : (pendingFees || []).reduce(
                (sum: number, fee: PendingFee) => sum + (fee.amount || 0),
                0,
              )
        // Distinct months owed, not a raw pendingFees count -- Late Fee is
        // its own entry now, so one unpaid month is 2 entries (Tuition Fee
        // + Late Fee) but should still read as "1 month", matching the Due
        // Months column right next to this one.
        const monthCount = new Set(
          (pendingFees || [])
            .filter((fee: PendingFee) => fee.month && fee.month !== "N/A")
            .map((fee: PendingFee) => fee.month),
        ).size
        const count = monthCount || pendingFees?.length || 0

        return (
          <div>
            <span className="badge badge-soft-danger me-1">
              {count} month{count !== 1 ? "s" : ""}
            </span>
            <span className="fw-semibold">
              Rs. {totalPending.toLocaleString()}
            </span>
          </div>
        )
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      width: 140,
      // Every row in this list is a defaulter by definition, so a column that
      // always read "Defaulter" told nobody anything. It carries the student's
      // real standing instead — struck-off students stay on this list because
      // the fees are still owed, and this is what makes them recognisable.
      render: (_text: string, record: Defaulter) => {
        const active = !record.status || record.status === "admitted"
        return (
          <span className={`badge ${active ? "badge-soft-warning" : "badge-soft-danger"}`}>
            {record.statusLabel || "Active"}
          </span>
        )
      },
    },
    {
      title: "Action",
      dataIndex: "action",
      // No fixed width -- every other column has an explicit px width that
      // sums to less than most screens, so without this being flexible the
      // table left a permanent blank gap on the right on screen. Letting
      // Action (the last column) stretch closes that gap.
      // Buttons only, no data -- doesn't belong on a printed roster and its
      // column was eating real width, contributing to the print overflow.
      className: "no-print",
      onHeaderCell: () => ({ className: "no-print" } as any),
      render: (_status: string, record: Defaulter) => {
        return (
          <div className="d-flex align-items-center">
            <Link
              to="#"
              className="btn btn-light me-2"
              data-bs-toggle="modal"
              data-bs-target="#create_fee_modal"
              onClick={() => {
                const student = convertDefaulterForModal(record)
                setSelectedStudent(student)
              }}
            >
              Collect Fees
            </Link>
            <Link
              to="#"
              className={`btn btn-primary ${!selectedDueDate ? "disabled" : ""}`}
              onClick={() => {
                if (selectedDueDate) {
                  const student = convertDefaulterForPrint(record)
                  handlePrintSingleNotice(student)
                }
              }}
              style={{
                pointerEvents: !selectedDueDate ? "none" : "auto",
                opacity: !selectedDueDate ? 0.6 : 1,
              }}
              title={
                !selectedDueDate
                  ? "Please select a due date first"
                  : "Print Notice"
              }
            >
              Print Notice
            </Link>
          </div>
        )
      },
    },
  ]

  // The Action column just doesn't exist for the print/PDF render -- see
  // isPrintingList above for why CSS-only hiding wasn't enough.
  const columns = isPrintingList
    ? allColumns.filter(c => c.title !== "Action")
    : allColumns

  const onFeeModalClose = () => {
    setSelectedStudent(null)
  }

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="page-title mb-1">Defaulters List</h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.adminDashboard}>Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to="#">Finance & Accounts</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Defaulters
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
              <TooltipOption
                onRefresh={fetchAllData}
                onExportPdf={triggerListPrint}
                onExportExcel={handleExportExcel}
              />

              <Link
                to="#"
                className={`btn btn-primary me-2 ${!canPrintAllNotices ? "disabled" : ""}`}
                onClick={handlePrintAllNotices}
                title={
                  !selectedDueDate
                    ? "Please select a due date first"
                    : recordsToPrint.length === 0
                      ? "Search or load defaulters first"
                      : selectedDefaulters.length > 0
                        ? `Print only the ${selectedDefaulters.length} checked row(s)`
                        : "Print every loaded record"
                }
                style={{
                  pointerEvents: !canPrintAllNotices ? "none" : "auto",
                  opacity: !canPrintAllNotices ? 0.6 : 1,
                }}
              >
                <i className="ti ti-printer me-2" />
                {selectedDefaulters.length > 0
                  ? `Print Notices (${selectedDefaulters.length})`
                  : "Print All Notices"}
              </Link>
            </div>
          </div>

          {/* The monthly sweep flags students who meet a struck-off rule but
              never acts on them. This is where someone decides. It only shows
              when there is something waiting, so it cannot become wallpaper. */}
          {pendingStruckOff > 0 && (
            <div className="alert alert-light border-start border-danger border-3 text-dark d-flex flex-wrap align-items-center justify-content-between gap-2">
              <div>
                <strong>{pendingStruckOff} student(s) flagged for struck-off.</strong>{" "}
                Nobody has been taken off the rolls — these are waiting for you to
                confirm or dismiss each one.
              </div>
              <button className="btn btn-sm btn-danger" onClick={() => setShowStruckOffReview(true)}>
                Review
              </button>
            </div>
          )}

          <div className="card">
            <div className="card-header pb-3">
              <div>
                <h4 className="mb-1">Defaulters List</h4>
                <p className="text-muted small mb-3">
                  Choose System (Class, Section, Status, Notice and Due Date
                  are all optional -- an empty Class means every class under
                  that System), then click <strong>Search</strong> to load
                  defaulters. Every matching record loads and prints at once
                  -- no pagination. Or use <strong>Quick Search</strong> to
                  jump straight to one student by GR number or name.
                </p>
              </div>
              <div className="d-flex align-items-end flex-wrap w-100">
                <div className="mb-3 me-2">
                  <label className="form-label fw-semibold">System</label>
                  <CommonSelect
                    className="select"
                    options={systems}
                    value={systems.find(item => item._id === draftSystem)}
                    getOptionValue={item => item._id}
                    getOptionLabel={item => item.name}
                    onChange={handleSystemChange}
                    placeholder="Select System"
                  />
                </div>
                <div className="mb-3 me-2">
                  <label className="form-label fw-semibold">
                    Class <span className="text-muted fw-normal">(optional)</span>
                  </label>
                  <CommonSelect
                    className="select"
                    options={classOptions}
                    value={classOptions.find(opt => opt.value === draftClassId)}
                    onChange={handleClassChange}
                    placeholder={
                      classesLoading
                        ? "Loading..."
                        : !draftSystem
                          ? "Select System first"
                          : "All classes"
                    }
                    isDisabled={!draftSystem || classesLoading}
                  />
                </div>
                <div className="mb-3 me-2">
                  <label className="form-label fw-semibold">
                    Section <span className="text-muted fw-normal">(optional)</span>
                  </label>
                  <CommonSelect
                    className="select"
                    options={sectionOptions}
                    value={sectionOptions.find(opt => opt.value === draftSection)}
                    onChange={handleSectionChange}
                    placeholder={
                      !draftClassId
                        ? "Select Class first"
                        : sectionOptions.length === 0
                          ? "No sections"
                          : "All sections"
                    }
                    isDisabled={!draftClassId || sectionOptions.length === 0}
                  />
                </div>
                <div className="mb-3 me-2">
                  <label className="form-label fw-semibold">Status</label>
                  <CommonSelect
                    className="select"
                    options={STATUS_OPTIONS}
                    value={STATUS_OPTIONS.find(
                      opt => opt.value === selectedStatus,
                    )}
                    onChange={item =>
                      item && setSelectedStatus(item.value as string)
                    }
                  />
                </div>
                <div className="mb-3 me-2">
                  <label className="form-label fw-semibold">Notice</label>
                  <CommonSelect
                    className="select"
                    options={NOTICE_TYPE_OPTIONS}
                    value={NOTICE_TYPE_OPTIONS.find(
                      opt => opt.value === selectedNoticeType,
                    )}
                    onChange={item =>
                      item && setSelectedNoticeType(item.value as string)
                    }
                  />
                </div>
                <div className="mb-3 me-2">
                  <label className="form-label fw-semibold">
                    Due Date <span className="text-muted fw-normal">(for print)</span>
                  </label>
                  <input
                    type="date"
                    className="form-control"
                    value={
                      selectedDueDate
                        ? selectedDueDate.toISOString().split("T")[0]
                        : ""
                    }
                    onChange={e => {
                      const selectedDate = e.target.value
                        ? new Date(e.target.value)
                        : null
                      if (
                        selectedDate &&
                        selectedDate >= new Date(new Date().setHours(0, 0, 0, 0))
                      ) {
                        setSelectedDueDate(selectedDate)
                      } else if (!e.target.value) {
                        setSelectedDueDate(null)
                      }
                    }}
                    min={new Date().toISOString().split("T")[0]}
                    title="Select a future date for print display"
                  />
                </div>
                <div className="mb-3 d-flex gap-2">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleSearchClick}
                    disabled={!canSearch}
                    title={canSearch ? "Search defaulters" : "Select a System first"}
                  >
                    <i className="ti ti-search me-1" />
                    Search
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={handleResetFilters}
                    title="Reset filters"
                  >
                    <i className="ti ti-refresh" />
                  </button>
                </div>
                <div className="mb-3 ms-3 ps-3 border-start">
                  <label className="form-label fw-semibold">
                    Quick Search <span className="text-muted fw-normal">(GR number or name)</span>
                  </label>
                  <div className="d-flex gap-2">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="e.g. 135226"
                      value={quickSearchValue}
                      onChange={e => setQuickSearchValue(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === "Enter") handleQuickSearch()
                      }}
                      style={{ minWidth: 180 }}
                    />
                    <button
                      type="button"
                      className="btn btn-outline-primary"
                      onClick={handleQuickSearch}
                      disabled={!quickSearchValue.trim()}
                      title="Search any single student directly, no System/Class needed"
                    >
                      <i className="ti ti-search" />
                    </button>
                  </div>
                </div>
              </div>
              {hasSearched && (
                <div className="text-muted small mt-1">
                  {loading
                    ? "Loading…"
                    : `${totalDefaulters} defaulter${totalDefaulters === 1 ? "" : "s"} found, totalling Rs. ${totalPendingAmount.toLocaleString()}${
                        selectedDefaulters.length
                          ? `, ${selectedDefaulters.length} selected`
                          : ""
                      }`}
                </div>
              )}
            </div>
            <div
              className="card-body p-0 py-3 printable-content"
              ref={componentRef}
            >
              <div className="print-only-header">
                <div className="school-brand">
                  <img src={currentBranch?.blackLogoUrl || "/assets/img/icons/logo.png"} alt={campusBrandName(currentBranch)} />
                  <strong>{campusBrandName(currentBranch)}{campusBranchLabel(currentBranch) ? ` - ${campusBranchLabel(currentBranch)}` : ""}</strong>
                </div>
                <div className="report-title">Fee Defaulters List</div>
                <div className="print-date">{new Date().toLocaleDateString("en-GB")}</div>
              </div>
              {!hasSearched && !loading ? (
                <div className="text-center py-5 text-muted">
                  <i className="ti ti-list-search" style={{ fontSize: 36 }} />
                  <p className="mt-2 mb-0">
                    Select a System (and optionally Class/Section) above and
                    click Search, or use Quick Search to jump straight to one
                    student.
                  </p>
                </div>
              ) : data.length === 0 && !loading ? (
                <div className="text-center py-5">
                  <h4>No defaulters found</h4>
                  <p className="text-muted">
                    There are no students with pending fees matching your
                    criteria.
                  </p>
                </div>
              ) : (
                <Table
                  setSearchText={setSearchText}
                  toolbarRightExtra={
                    // The sort dropdown that used to sit here offered Newest
                    // First / Oldest First / Name A-Z, none of which the
                    // defaulters endpoint has ever read — it always sorted
                    // server-side and ignored the parameter. It has been
                    // replaced by a note, because this list now comes back in
                    // register order (class, then section, then name) and the
                    // printed notices have to come off in that same order.
                    <div className="mb-3 text-muted small align-self-center">
                      Sorted by class, section and name
                    </div>
                  }
                  dataSource={data}
                  columns={columns}
                  loading={loading}
                  hidePagination
                  Selection={true}
                  onSelectionChange={(_keys: any[], rows: Defaulter[]) => {
                    setSelectedDefaulters(rows || [])
                  }}
                />
              )}
              {/* Print/PDF-only grand total -- hidden on screen (the "N
                  defaulters found, totalling Rs. X" line above already
                  covers that), shown only on the printed page/export. */}
              {data.length > 0 && (
                <div className="print-only-total" style={{ display: "none" }}>
                  TOTAL ({data.length} defaulter{data.length === 1 ? "" : "s"}): Rs. {totalPendingAmount.toLocaleString()}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <CollectFeesModal
        onClose={onFeeModalClose}
        studentData={selectedStudent as any}
        selectedSystem={appliedFilters?.system}
        feeGroup={appliedFilters?.feeGroup}
      />

      <div style={{ display: showPrintNotice ? "block" : "none" }}>
        <PrintNotice
          student={selectedStudentForPrint!}
          isAllStudents={printAllStudents}
        />
      </div>

      {showStruckOffReview && (
        <StruckOffReviewModal
          branchId={currentBranch?._id}
          onClose={() => setShowStruckOffReview(false)}
          onChanged={loadPendingStruckOff}
        />
      )}
    </>
  )
}

export default DefaultersList
